import { useFormatoNoBlur } from '@/hooks/use-formato-no-blur';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { PrecificarDto } from '@triade/contracts';
import { useCondicoesPagamento } from '@/api/condicoes-pagamento';
import { usePoliticaPreco } from '@/api/liberacao';
import { useItem, useItens } from '@/api/itens';
import { useGravarPrecificacao, useItemCusto, useSimulacao } from '@/api/precificacao';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { useTabelaCondicoes } from '@/api/tabela-condicao';
import { useExibicao } from '@/api/exibicao';
import { usePode } from '@/api/me';
import { AvisoMarcaFormacao } from '@/components/marcas/marca-revisao';
import { OrigemLucro } from '@/components/origem-lucro';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { SeletorCategoria } from '@/components/categorias/seletor-categoria';
import { ProfitBadge } from '@/components/profit-badge';
import { BlocoMargemContribuicao } from '@/components/margem-contribuicao';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarCusto, formatarFracaoPercentual, formatarMarkup } from '@/lib/format';
import { useSessao } from '@/lib/session';
import { AvisosFormacao, ErroFormacao } from './avisos-formacao';
import { PublicacoesPainel } from '@/components/publicacoes/publicacoes-painel';
import { notaMarkupSobreSugerido, PainelPreco, PainelPrecoEsqueleto } from './painel-preco';
import { definirFiltrosTela, limparFiltrosTela } from '@/components/copiloto/contexto-tela-store';
import { CustoProduto, FICHA_ATIVA, SeletorFicha } from './custo-produto';
import { LiberacaoItem } from './liberacao-item';
import { LiberacaoLote } from './liberacao-lote';
import { DispensaMarcasLote } from './dispensa-marcas-lote';
import { lerFormacao } from './tipos';
import { BlocoReformaFormacao } from './bloco-reforma';
import { PainelReversoDialog } from './painel-reverso';
import { BotaoRelatorios } from '@/relatorios';

interface Formulario {
  item: ItemResumo | null;
  tabelaId: string;
  condicaoId: string;
  data: string;
  /** Texto digitado, como o usuario escreveu ("25,00"). */
  praticado: string;
  /** Sessao 37: ficha do item so para SIMULAR ('' = a ficha ativa). Nao entra no "alterado" nem no gravar. */
  fichaId: string;
}

/** Data local de hoje (YYYY-MM-DD) para o campo de data; so formata, nao calcula preco. */
function hoje(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

const CAMPOS: Array<keyof Formulario> = ['item', 'tabelaId', 'condicaoId', 'data', 'praticado'];

const MOTIVO_FICHA_NAO_ATIVA = 'Simulando com uma ficha que não é a ativa: marque-a como a ficha ativa para gravar, liberar ou publicar.';

function igual(a: Formulario, b: Formulario, campo: keyof Formulario): boolean {
  if (campo === 'item') return a.item?.id === b.item?.id;
  if (campo === 'praticado') return (decimalDigitado(a.praticado) ?? '') === (decimalDigitado(b.praticado) ?? '');
  return a[campo] === b[campo];
}

function paraDto(f: Formulario, praticadoValido: string | null): PrecificarDto | null {
  if (!f.item || !f.tabelaId || !f.condicaoId || !f.data) return null;
  return {
    itemId: f.item.id,
    tabelaId: f.tabelaId,
    condicaoId: f.condicaoId,
    data: f.data,
    ...(praticadoValido !== null ? { praticado: praticadoValido } : {}),
    ...(f.fichaId ? { fichaId: f.fichaId } : {}),
  };
}

const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const TRIGGER = 'h-10 w-full rounded-[10px] bg-card';

/** Pre-selecao vinda da URL (ex.: fim do onboarding, ou "Carregar na simulacao" da Liberacao): ids, e o praticado (decimal com ponto). */
export interface FormacaoInicial {
  itemId?: string;
  tabelaId?: string;
  condicaoId?: string;
  praticado?: string;
}

const DICA_PRATICADO = 'Informe o preço praticado (com IPI, a prazo) para ver o lucro simulado e o markup implícito.';
const DICA_NEGOCIADO = 'Política Negociado: não há praticado nesta formação.';
const DICA_SEM_SELECAO = 'Escolha o produto ou serviço, a tabela de preço e a condição de pagamento para calcular o preço sugerido.';

export function FormacaoPage({ inicial }: { inicial?: FormacaoInicial } = {}) {
  const { empresaId } = useSessao();
  // Registrar um novo praticado exige praticado.gravar (a API nega o POST; aqui so se oferece o caminho).
  const { pode } = usePode();
  const podePraticado = pode('praticado.gravar');
  // Politica NEGOCIADO: sem praticado na formacao nem liberacao (o orcamento parte do sugerido).
  const negociado = usePoliticaPreco().data?.politicaPreco === 'NEGOCIADO';
  const [vazio] = useState<Formulario>(() => ({
    item: null,
    tabelaId: inicial?.tabelaId ?? '',
    condicaoId: inicial?.condicaoId ?? '',
    data: hoje(),
    praticado: decimalParaTextoCampo(inicial?.praticado ?? null),
    fichaId: '',
  }));
  const [form, setForm] = useState<Formulario>(vazio);
  const itemInicial = useItem(inicial?.itemId);
  useEffect(() => {
    const it = itemInicial.data;
    if (it) setForm((f) => (f.item ? f : { ...f, item: { id: it.id, codigo: it.codigo, descricao: it.descricao, tipo: it.tipo } }));
  }, [itemInicial.data]);
  const [salvo, setSalvo] = useState<Formulario | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [termo, setTermo] = useState('');
  const termoBusca = useDebounced(termo.trim(), 250);

  // Categoria (com as subcategorias) ou SEM; so restringe a busca de itens. Nao muda a formacao.
  const [categoriaBusca, setCategoriaBusca] = useState<string | undefined>(undefined);
  const itens = useItens({ q: termoBusca || undefined, ativo: true, comerciavel: true, limit: 20, categoriaId: categoriaBusca });
  const tabelas = useTabelasPreco({ ativa: true, limit: 200 });
  const condicoes = useCondicoesPagamento({ ativa: true, limit: 200 });
  // Bloco opcional por empresa (Configuracoes > Empresa); padrao ligado.
  const mostraReforma = useExibicao().data?.mostrarReformaFormacao ?? true;

  // Ao escolher a tabela, a condicao padrao dela vem selecionada. Vale ate o usuario escolher a condicao a mao; uma condicao vinda
  // da URL ("Carregar na simulacao") conta como escolhida. Sem condicao padrao na tabela, a condicao atual fica como esta.
  const condicaoEscolhida = useRef(Boolean(inicial?.condicaoId));
  const padraoDaTabela = useTabelaCondicoes(form.tabelaId || undefined).data?.find((c) => c.isDefault)?.condicaoId;
  useEffect(() => {
    if (!padraoDaTabela || condicaoEscolhida.current) return;
    setForm((f) => (f.condicaoId === padraoDaTabela ? f : { ...f, condicaoId: padraoDaTabela }));
  }, [form.tabelaId, padraoDaTabela]);

  const praticadoValido = decimalDigitado(form.praticado);
  const praticadoEfetivo = negociado ? null : praticadoValido;
  const praticadoDebounced = useDebounced(praticadoEfetivo, 400);
  const dto = useMemo(
    () => paraDto(form, praticadoDebounced),
    [form, praticadoDebounced],
  );
  const simulacao = useSimulacao(dto);
  // Custo e composicao do produto assim que ele e escolhido (antes de tabela e condicao), com as fichas do item (Sessao 37).
  const podeCusto = pode('precificacao.simular') && pode('custo.ver');
  const itemCusto = useItemCusto(form.item && podeCusto ? { itemId: form.item.id, ...(form.data ? { data: form.data } : {}), ...(form.fichaId ? { fichaId: form.fichaId } : {}) } : null);
  const semFichaAtiva = itemCusto.data?.origem === 'SEM_CUSTO' && itemCusto.data.motivo === 'SEM_FICHA_ATIVA';
  // So simulacao: gravar, liberar e publicar ficam desabilitados enquanto a ficha escolhida nao e a ativa.
  const simulandoOutra = form.fichaId !== '';
  const gravar = useGravarPrecificacao();

  const lida = simulacao.data ? lerFormacao(simulacao.data) : null;
  const dtoAtual = paraDto(form, praticadoEfetivo);
  const sincronizado = dto !== null && JSON.stringify(dto) === JSON.stringify(dtoAtual);
  const praticadoInvalido = !negociado && form.praticado.trim() !== '' && praticadoValido === null;

  const base = salvo ?? vazio;
  const alterados = CAMPOS.filter((c) => !igual(form, base, c)).length;
  const dirty = dtoAtual !== null && alterados > 0 && !simulandoOutra;
  const bloqueio = useBlockerDirty(dirty);
  const pronto = dirty && sincronizado && simulacao.isSuccess && !simulacao.isFetching && lida !== null && !praticadoInvalido;

  const praticadoFormatado = useFormatoNoBlur(form.praticado, 'dinheiro');
  const set = useCallback(<K extends keyof Formulario>(campo: K, valor: Formulario[K]) => setForm((f) => ({ ...f, [campo]: valor })), []);
  // Selecao para o copiloto (fora da URL): so ids e o praticado digitado; limpa ao sair da tela.
  useEffect(() => {
    definirFiltrosTela('formacao', { itemId: form.item?.id, tabelaId: form.tabelaId, condicaoId: form.condicaoId, praticado: form.praticado });
  }, [form.item?.id, form.tabelaId, form.condicaoId, form.praticado]);
  useEffect(() => () => limparFiltrosTela('formacao'), []);

  async function salvar() {
    if (!pronto || !dtoAtual || simulandoOutra) return;
    setErroSalvar(null);
    try {
      await gravar.mutateAsync(dtoAtual);
      setSalvo(form);
      setSalvoEm(new Date());
    } catch (e) {
      setErroSalvar(e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
    }
  }

  const nomeTabela = (id: string | undefined) => tabelas.data?.find((t) => t.id === id)?.nome ?? '—';
  const nomeCondicao = (id: string | undefined) => condicoes.data?.find((c) => c.id === id)?.nome ?? '—';

  const cadastrosErro = tabelas.error ?? condicoes.error;
  const cadastrosCarregando = tabelas.isPending || condicoes.isPending;
  const semEmpresa = empresaId === null;
  // Botao "Preço pelo resultado" (pop-up): desabilitado sem item, tabela ou condicao (dto null).
  const botaoReverso = pode('precificacao.simular') && pode('custo.ver') ? (
    <PainelReversoDialog
      referencia={dto !== null ? { itemId: dto.itemId, tabelaId: dto.tabelaId, condicaoId: dto.condicaoId, data: dto.data } : null}
      podeUsar={!negociado}
      onUsarComoPraticado={(preco) => set('praticado', decimalParaTextoCampo(preco))}
      idCampoRetorno="f-pratic"
    />
  ) : null;

  // Sugerido e Praticado: sempre lado a lado (50/50 a partir de md). Sem valor, cada um mostra o esqueleto do proprio PainelPreco.
  const esqueletoSugerido = (dica: string) => (
    <PainelPrecoEsqueleto titulo="Preço sugerido" nome="Preço sugerido" rotuloPreco="Preço sugerido" dica={dica} className="min-w-0" />
  );
  const esqueletoPraticado = (
    <PainelPrecoEsqueleto
      titulo={
        <>
          Preço praticado <span className="font-normal text-ink-muted">· simulação</span>
        </>
      }
      nome="Preço praticado"
      rotuloPreco="Preço praticado"
      rotuloMarkup="Markup implícito"
      dica={negociado ? DICA_NEGOCIADO : DICA_PRATICADO}
      className="min-w-0"
    />
  );
  const paresSemValor = (dicaSugerido: string) => (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="sugerido-praticado" aria-busy={simulacao.isFetching || undefined}>
      {esqueletoSugerido(dicaSugerido)}
      {esqueletoPraticado}
    </div>
  );

  let resultado: React.ReactNode;
  if (semEmpresa) {
    resultado = <Vazio>Escolha a empresa no menu lateral para operar.</Vazio>;
  } else if (cadastrosErro) {
    resultado = (
      <ErroFormacao
        erro={cadastrosErro}
        onTentar={() => {
          void tabelas.refetch();
          void condicoes.refetch();
        }}
      />
    );
  } else if (dtoAtual === null) {
    resultado = paresSemValor(DICA_SEM_SELECAO);
  } else if (simulacao.isError && semFichaAtiva) {
    // Produto sem ficha ativa: o aviso claro (com o link) esta no painel de custo, acima; aqui so o esqueleto.
    resultado = paresSemValor('Sem ficha técnica ativa: escolha a ficha para calcular o preço.');
  } else if (simulacao.isError) {
    resultado = <ErroFormacao erro={simulacao.error} onTentar={() => void simulacao.refetch()} />;
  } else if (!lida) {
    resultado = paresSemValor(simulacao.isPending || simulacao.isFetching ? 'Calculando…' : 'Sem resultado.');
  } else {
    const r = lida.resultado;
    const c = lida.composicao;
    const minimo = c.lucroMinimo ?? '0';
    const prat = c.praticado;
    resultado = (
      <div className={`flex flex-col gap-5 ${simulacao.isFetching ? 'opacity-70' : ''}`} aria-busy={simulacao.isFetching}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="sugerido-praticado">
          <PainelPreco
            className="min-w-0"
            titulo="Preço sugerido"
            badge={
              <span className="rounded-full bg-line-soft px-2.5 py-0.5 text-caption font-medium text-ink-body">
                Lucro na meta · {formatarFracaoPercentual(r.desiredProfitRate)}
              </span>
            }
            equacao={c.sugerido}
            despesasVariaveis={c.despesasVariaveis}
            despesasVariaveisTotal={c.despesasVariaveisTotal}
            markupNota={`${formatarMarkup(c.sugerido.markupSemIpi ?? '')} sem IPI · IPI ${formatarFracaoPercentual(r.ipiRate)}`}
            rotuloPreco="Preço sugerido"
            extra={
              lida.margemContribuicao && (
                <BlocoMargemContribuicao
                  titulo="Sugerido"
                  bloco={lida.margemContribuicao.sugerido}
                  custoProcessoAbsorvido={lida.margemContribuicao.custoProcessoAbsorvido}
                />
              )
            }
          />
          {prat && r.practiced ? (
            <PainelPreco
              className="min-w-0"
              titulo={
                <>
                  Preço praticado <span className="font-normal text-ink-muted">· simulação</span>
                </>
              }
              badge={<ProfitBadge lucro={r.practiced.simulatedProfit} lucroExibido={prat.segmentos.lucro.fracao} meta={r.desiredProfitRate} minimo={minimo} faixa={lida.faixaPraticado} />}
              equacao={prat}
              despesasVariaveis={c.despesasVariaveis}
              despesasVariaveisTotal={c.despesasVariaveisTotal}
              rotuloMarkup="Markup implícito"
              markupNota={notaMarkupSobreSugerido(prat.markupSobreSugerido)}
              rotuloPreco="Preço praticado"
              extra={
                lida.margemContribuicao?.praticado && (
                  <BlocoMargemContribuicao
                    titulo="Praticado"
                    bloco={lida.margemContribuicao.praticado}
                    custoProcessoAbsorvido={lida.margemContribuicao.custoProcessoAbsorvido}
                  />
                )
              }
            />
          ) : (
            esqueletoPraticado
          )}
        </div>
        <OrigemLucro desejado={lida.lucroDesejadoOrigem} minimo={lida.lucroMinimoOrigem} className="-mt-2" />
        {lida.marcaRevisao && (
          <AvisoMarcaFormacao
            marca={lida.marcaRevisao}
            podeRegistrar={podePraticado}
            onRegistrar={() => document.getElementById('f-pratic')?.focus()}
            podeDispensar={pode('marca.dispensar')}
          />
        )}
        <AvisosFormacao
          avisos={lida.avisos}
          custoFracao={c.sugerido.segmentos.custo.fracao}
          margemSugerido={c.sugerido.margemEfetivaAPrazo}
          margemPraticado={prat?.margemEfetivaAPrazo ?? null}
        />
      </div>
    );
  }

  const chipsFixos = lida && (
    <div className="col-span-full flex flex-wrap gap-2 border-t border-dashed border-border pt-3" data-testid="chips-formacao">
      {[
        `IPI ${formatarFracaoPercentual(lida.resultado.ipiRate)} · por fora`,
        `Taxa financeira ${formatarFracaoPercentual(lida.snapshot.company.financialCostRate)} a.m.`,
        `Despesa fixa ${formatarFracaoPercentual(lida.resultado.fixedExpenseRate)} · parâmetros da empresa`,
        `Lucro desejado ${formatarFracaoPercentual(lida.resultado.desiredProfitRate)} · mínimo ${formatarFracaoPercentual(lida.composicao.lucroMinimo ?? '0')}`,
      ].map((t) => (
        <span key={t} className="rounded-full bg-line-soft px-2.5 py-1 text-caption text-ink-body">
          {t}
        </span>
      ))}
    </div>
  );

  const fichaSimulada = itemCusto.data?.fichaUsada ?? null;

  return (
    <div className="flex flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-small text-ink-muted">
            Precificar <span aria-hidden="true">/</span> Formação de preço
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Formação de preço</h1>
          <p className="mt-1 text-ink-secondary">Calcule o preço sugerido e simule o praticado antes de vender.</p>
        </div>
        <BotaoRelatorios
          tela="formacao"
          params={{
            item: form.item?.codigo,
            tabela: form.tabelaId ? nomeTabela(form.tabelaId) : undefined,
            condicao: form.condicaoId ? nomeCondicao(form.condicaoId) : undefined,
          }}
          dados={simulacao.isPlaceholderData ? undefined : simulacao.data}
        />
      </div>

      <div className="flex flex-col gap-5">
        <section aria-label="Seleção" className="grid grid-cols-1 gap-3.5 rounded-2xl border border-border bg-card p-4 md:grid-cols-2 xl:grid-cols-4">
          <div className="min-w-0">
            <SeletorCategoria id="f-categoria" rotulo="Categoria" comTodas value={categoriaBusca} onChange={setCategoriaBusca} className="h-10 w-full" />
          </div>
          <div className="min-w-0">
            <Label htmlFor="f-produto" className={ROTULO}>
              Produto ou serviço
            </Label>
            <ItemCombobox
              id="f-produto"
              valor={form.item}
              onSelecionar={(it) => setForm((f) => ({ ...f, item: it, fichaId: '' }))}
              itens={itens.data ?? []}
              carregando={itens.isFetching}
              erro={itens.error?.message ?? null}
              termo={termo}
              onTermoChange={setTermo}
              disabled={semEmpresa}
            />
          </div>
          <div>
            <Label id="r-ficha" htmlFor="f-ficha" className={ROTULO}>
              Ficha técnica
            </Label>
            <SeletorFicha
              custo={itemCusto.data}
              valor={form.fichaId || FICHA_ATIVA}
              onChange={(v) => set('fichaId', v === FICHA_ATIVA ? '' : v)}
              desabilitado={semEmpresa}
            />
          </div>
          <div>
            <Label htmlFor="f-tabela" className={ROTULO}>
              Tabela de preço
            </Label>
            <Select
              value={form.tabelaId}
              onValueChange={(v) => {
                condicaoEscolhida.current = false;
                set('tabelaId', v);
              }} disabled={semEmpresa || cadastrosCarregando}>
              <SelectTrigger id="f-tabela" className={TRIGGER}>
                <SelectValue placeholder={tabelas.isPending ? 'Carregando…' : 'Escolha a tabela'} />
              </SelectTrigger>
              <SelectContent>
                {(tabelas.data ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {tabelas.data?.length === 0 && (
              <p className="mt-1 text-caption text-ink-muted">
                Nenhuma tabela de preço ativa. Crie em{' '}
                <Link to="/cadastros/tabelas" className="font-medium text-primary hover:underline">
                  Cadastros › Tabelas de preço
                </Link>
                .
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="f-cond" className={ROTULO}>
              Condição de pagamento
            </Label>
            <Select
              value={form.condicaoId}
              onValueChange={(v) => {
                condicaoEscolhida.current = true;
                set('condicaoId', v);
              }} disabled={semEmpresa || cadastrosCarregando}>
              <SelectTrigger id="f-cond" className={TRIGGER}>
                <SelectValue placeholder={condicoes.isPending ? 'Carregando…' : 'Escolha a condição'} />
              </SelectTrigger>
              <SelectContent>
                {(condicoes.data ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {condicoes.data?.length === 0 && (
              <p className="mt-1 text-caption text-ink-muted">
                Nenhuma condição de pagamento ativa. Crie em{' '}
                <Link to="/cadastros/condicoes" className="font-medium text-primary hover:underline">
                  Cadastros › Condições de pagamento
                </Link>
                .
              </p>
            )}
          </div>
          <div>
            <Label htmlFor="f-data" className={ROTULO}>
              Data da operação
            </Label>
            <Input id="f-data" type="date" value={form.data} onChange={(e) => set('data', e.target.value)} disabled={semEmpresa} />
          </div>
          {!negociado && (
          <div>
            <Label htmlFor="f-pratic" className={ROTULO}>
              Preço praticado
            </Label>
            <div className="flex items-center gap-2">
            <div className="flex h-10 min-w-0 flex-1 items-center gap-1.5 rounded-[10px] border border-input bg-card px-3 focus-within:border-primary focus-within:shadow-focus">
              <span aria-hidden="true" className="text-ink-muted">
                R$
              </span>
              <input
                id="f-pratic"
                type="text"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0,00"
                {...praticadoFormatado}
                onChange={(e) => set('praticado', e.target.value)}
                aria-invalid={praticadoInvalido || undefined}
                aria-describedby={praticadoInvalido ? 'e-pratic' : undefined}
                disabled={semEmpresa}
                className="tabular w-full border-0 bg-transparent font-semibold outline-none"
              />
            </div>
            {botaoReverso}
            </div>
            {praticadoInvalido && (
              <p id="e-pratic" role="alert" className="mt-1 text-caption text-danger-ink">
                Informe um valor como 25,00.
              </p>
            )}
          </div>
          )}
          {negociado && botaoReverso && <div>{botaoReverso}</div>}
          {chipsFixos}
        </section>
  
        {form.item && podeCusto && <CustoProduto custo={itemCusto.data} carregando={itemCusto.isFetching} />}

        {simulandoOutra && (
          <section role="status" data-testid="faixa-simulando-ficha" className="rounded-xl border border-warning/40 bg-warning-bg px-4 py-3 text-small text-warning-ink">
            Simulando com a ficha {fichaSimulada ? `versão ${fichaSimulada.versao}` : 'escolhida'} <strong>(não é a ativa)</strong>. {MOTIVO_FICHA_NAO_ATIVA}{' '}
            {form.item && (
              <Link to="/custos/fichas-tecnicas" search={{ itemId: form.item.id } as never} className="font-medium underline">
                Abrir as fichas do produto
              </Link>
            )}
          </section>
        )}

        {resultado}
      </div>

      {mostraReforma && (lida?.reforma || dto !== null) && <BlocoReformaFormacao reforma={lida?.reforma ?? null} porRegimeDto={dto} />}

      {/* Sessao 37: simulando com uma ficha que nao e a ativa, o que grava (liberacao, publicacao) fica desabilitado, com o motivo na faixa acima. */}
      <fieldset disabled={simulandoOutra} title={simulandoOutra ? MOTIVO_FICHA_NAO_ATIVA : undefined} data-testid="acoes-que-gravam" className="contents">
        {podePraticado && !negociado && form.item && <LiberacaoItem item={form.item} />}

        {podePraticado && !negociado && <LiberacaoLote />}
        {podePraticado && !negociado && <DispensaMarcasLote />}

        <PublicacoesPainel />
      </fieldset>

      <RecordToolbar
        dirty={dirty}
        saving={gravar.isPending}
        savedAt={salvoEm}
        erro={erroSalvar}
        novo={salvo === null}
        canPrev={false}
        canNext={false}
        alteradosCount={dirty ? alterados : undefined}
        onList={() => document.getElementById('historico')?.scrollIntoView({ behavior: 'smooth' })}
        onSave={() => void salvar()}
        onNew={() => {
          condicaoEscolhida.current = Boolean(inicial?.condicaoId);
          setForm({ ...vazio, data: hoje() });
          setSalvo(null);
          setSalvoEm(null);
          setErroSalvar(null);
          setTermo('');
        }}
        onUndo={() => {
          setForm(salvo ?? vazio);
          setErroSalvar(null);
        }}
        onPrev={() => undefined}
        onNext={() => undefined}
        onDelete={() => setErroSalvar('formações gravadas são imutáveis e não podem ser excluídas')}
      />
      <SaidaComAlteracoesDialog {...bloqueio} />
    </div>
  );
}

function Vazio({ children, papel }: { children: React.ReactNode; papel?: 'status' }) {
  return (
    <p role={papel} className="rounded-2xl border border-dashed border-border bg-card px-5 py-10 text-center text-small text-ink-muted">
      {children}
    </p>
  );
}
